import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  getTaskById,
  updateTaskStatus,
  deleteTask,
} from "../../services/taskService";
import {
  getNotesByTask,
  addNote,
  deleteNote,
} from "../../services/taskNoteService";
import { priorityColor } from "../../utils/priorityColor";

const TaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const goBack = () => {
    navigate(-1);
  };
  useEffect(() => {
    async function fetchData() {
      try {
        const data = await getTaskById(id);
        setTask(data);
        const notes = await getNotesByTask(id);
        setNotes(notes);
      } catch (error) {
        console.error("Failed to fetch task:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  /* ===================== ADDED HANDLERS ===================== */

  const handlePriorityChange = async (value) => {
    setUpdating(true);
    try {
      await updateTaskStatus(id, { priority: value });
      setTask((prev) => ({ ...prev, priority: value }));
    } catch (err) {
      alert("Failed to update priority");
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = async (value) => {
    setUpdating(true);
    try {
      await updateTaskStatus(id, { status: value });
      setTask((prev) => ({ ...prev, status: value }));
    } catch (err) {
      alert("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      await deleteTask(id);
      goBack();
    } catch {
      alert("Failed to delete task");
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    const result = await addNote({
      taskId: id,
      noteDate: new Date().toISOString().slice(0, 10),
      note: newNote,
    });

    if (result.success) {
      const updatedNotes = await getNotesByTask(id);
      setNotes(updatedNotes);
      setNewNote("");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm("Delete this note?")) {
      await deleteNote(noteId);
      const updatedNotes = await getNotesByTask(id);
      setNotes(updatedNotes);
    }
  };

  /* ========================================================== */

  if (loading) {
    return <div className="p-6 text-center">Loading task details...</div>;
  }

  if (!task) {
    return <div className="p-6 text-center text-red-500">Task not found.</div>;
  }

  return (
    <div className="p-0">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-bold">Task Detail</h1>

        <div className="flex gap-2">
          <Link
            to={`/tasks/edit/${task.id}`}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
          >
            Edit
          </Link>

          <button
            onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded"
          >
            Delete
          </button>

          <Link
            onClick={() => goBack()}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded"
          >
            Back
          </Link>
        </div>
      </div>

      {/* CONTENT */}
      <div className="bg-white p-6 rounded shadow space-y-4">
        <div>
          <h2 className="text-xl font-semibold mb-2">{task.name}</h2>
          <p className="text-gray-600 dark:text-gray-600">{task.description}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="font-semibold">Project:</span> {task.project.name}
          </div>

          <div>
            <span className="font-semibold">Assigned To:</span>{" "}
            {task.assignTo.name}
          </div>

          {/* PRIORITY (EDITABLE) */}
          <div>
            <span className="font-semibold">Priority:</span>{" "}
            <select
              value={task.priority}
              disabled={updating}
              onChange={(e) => handlePriorityChange(e.target.value)}
              className={`py-1 px-3 rounded-full text-white text-xs ${priorityColor(
                task.priority,
              )}`}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          {/* STATUS (EDITABLE) */}
          <div>
            <span className="font-semibold">Status:</span>{" "}
            <select
              value={task.status}
              disabled={updating}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="border px-2 py-1 rounded text-sm"
            >
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="on-hold">On Hold</option>
            </select>
          </div>

          <div>
            <span className="font-semibold">Start Date:</span> {task.startDate}
          </div>

          <div>
            <span className="font-semibold">Due Date:</span> {task.deadline}
          </div>
        </div>

        {/* NOTES SECTION */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h2 className="text-xl font-semibold mb-4">
            📒 Daily Progress Notes
          </h2>

          {notes.length === 0 ? (
            <p className="text-gray-500 text-sm">
              No notes yet. Start tracking your done things ✨
            </p>
          ) : (
            <ul className="space-y-3 mb-4">
              {notes.map((note) => (
                <li
                  key={note.id}
                  className="bg-yellow-50 border rounded-lg p-3 flex justify-between gap-4"
                >
                  <div>
                    <p className="text-gray-800">{note.note}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(note.targetDate).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteNote(note.id)}
                    className="text-red-600 text-xs hover:underline"
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* ADD NOTE */}
          <div className="mt-4">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={3}
              placeholder="What did you done today?"
              className="w-full border rounded-lg p-2 mb-2"
            />
            <button
              onClick={handleAddNote}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              + Add Note
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetail;
